import { Logger, LoggerService } from '@Shared/logger';
import { UrlUtil } from '@Shared/utils';
import { AUTH0_CONST } from '@Modules/auth_module';
import { AppleService } from '@Modules/apple_module';
import { PipelineContext, PipelineStep } from '@Modules/pipeline_module/_base';

/**
 * Terminal step for the /callback flow. When `auth_redirect` is present on /callback this
 * sends the user there (with the Apple News access_token appended when applicable). On any
 * other path it is a no-op so it is safe to chain unconditionally from upstream steps.
 */
export class HandleCallbackRedirectStep implements PipelineStep {
  private Logger: Logger = LoggerService.createLogger(
    'HandleCallbackRedirectStep',
  );

  constructor() {
    this.Logger.trace('****  CTOR ****');
  }

  async execute(context: PipelineContext): Promise<PipelineStep | null> {
    this.Logger.trace('Executing....', context);

    const authRedirect = UrlUtil.getQueryParam(AUTH0_CONST.authRedirectParam);
    if (!authRedirect) {
      this.Logger.trace('No auth_redirect param present, nothing to redirect');
      return null;
    }

    // They shouldn't be left in the address bar.
    UrlUtil.removeQueryParam(AUTH0_CONST.authRedirectParam);
    UrlUtil.removeQueryParam(AUTH0_CONST.loginSourceParam);

    try {
      const redirectUrl = context.isAppleCallback
        ? AppleService.Instance.buildPostAppleRedirectUrl(
            authRedirect,
            context.appleAccessToken ?? null,
          )
        : new URL(authRedirect);

      // Apple is legitimately cross-origin, has to stay on this site.
      if (
        !context.isAppleCallback &&
        redirectUrl.origin !== window.location.origin
      ) {
        this.Logger.warn(
          'Ignoring cross-origin auth_redirect',
          redirectUrl.origin,
        );
        return null;
      }

      if (redirectUrl.toString() === window.location.href) {
        this.Logger.trace('Already on the destination, nothing to redirect');
        return null;
      }

      this.Logger.trace('Redirecting to', redirectUrl.toString());
      window.location.assign(redirectUrl.toString());
    } catch (error) {
      this.Logger.error('Failed to redirect after authentication', error);
    }
    return null;
  }
}
